import { useEffect, useMemo, useRef } from "react"

import { requestCoviHighlight } from "@/lib/coviEvents"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../event-bus"
import type { EditableTestCase, PendingChanges } from "../model"
import { appendText, separateAppendedText } from "../utils/appendSeparator"
import {
  applyPendingRowAction,
  getPendingDiffSummaries,
  removePendingChangeAtIndex,
} from "../utils/copilotSidebarState"
import {
  publishCoviTestSetEditorSnapshot,
  registerCoviTestSetEditor,
  type CoviTestCasePatch,
  type CoviTestSetEditorAction,
  type CoviTestSetEditorResult,
} from "../utils/coviTestSetEditorBridge"
import { CLEAR_SENTINEL } from "../utils/pendingChangesValidation"

function truncate(value: string | null | undefined, maxLength = 44) {
  if (!value) {
    return null
  }
  return value.length > maxLength
    ? `${value.slice(0, maxLength).trimEnd()}...`
    : value
}

function testCaseLabel(testCase: EditableTestCase | undefined, index: number) {
  return (
    truncate(testCase?.description) ??
    truncate(testCase?.input_str) ??
    `Test case ${index + 1}`
  )
}

function actionTypeFor(action: CoviTestSetEditorAction) {
  switch (action.kind) {
    case "draft_test_cases":
    case "add_test_case":
      return "ADD_NEW"
    case "update_test_case":
    case "update_test_cases":
      return "EDIT"
    case "delete_test_case":
      return "DELETE"
  }
}

function editRow(change: CoviTestCasePatch): Record<string, unknown> {
  return {
    id: change.test_case_id,
    input_str: change.input_str,
    append_input_str: change.append_input_str,
    // An empty description means clear to Sofia but no change to the editor.
    description:
      change.description === "" ? CLEAR_SENTINEL : change.description,
    expected_behaviors: change.expected_behaviors,
    // An attribute patch; the pending-row merge applies it over the
    // case's current attributes, with null removing an attribute.
    expected_output_json: change.expected_output_json,
    input_type: change.input_type,
    script_turns: change.script_turns,
  }
}

function pendingTypeFor(action: CoviTestSetEditorAction) {
  switch (actionTypeFor(action)) {
    case "ADD_NEW":
      return "ADD"
    case "EDIT":
      return "EDIT"
    case "DELETE":
      return "DELETE"
  }
}

function rowsFor(action: CoviTestSetEditorAction): Record<string, unknown>[] {
  switch (action.kind) {
    case "draft_test_cases":
      return action.args.test_cases.map((testCase) => ({ ...testCase }))
    case "add_test_case":
      return [{ ...action.args }]
    case "update_test_case":
      return [editRow(action.args)]
    case "update_test_cases":
      return action.args.changes.map(editRow)
    case "delete_test_case":
      return [{ id: action.args.test_case_id }]
  }
}

/**
 * An edit row with its `append_input_str` resolved to a full input: the text
 * goes after the row's pending input, or its saved input when nothing is
 * pending, so a second append stacks while a repeat of the same one is a no-op.
 */
function withAppliedAppend(
  row: Record<string, unknown>,
  pending: PendingChanges | null,
  { testCases }: { testCases: EditableTestCase[] },
): Record<string, unknown> {
  const { append_input_str: addition, ...rest } = row
  if (typeof addition !== "string") {
    return rest
  }
  const pendingEdit =
    pending?.type === "EDIT"
      ? pending.data.find((edit) => edit.id === row.id)
      : undefined
  const saved = testCases.find((testCase) => testCase.id === row.id)
  const base =
    typeof pendingEdit?.input_str === "string"
      ? pendingEdit.input_str
      : (saved?.input_str ?? "")
  return {
    ...rest,
    input_str: appendText(base, addition),
  }
}

/** An edit row whose appended sentence is separated from the saved input. */
function withSeparatedAppend(
  row: Record<string, unknown>,
  { testCases }: { testCases: EditableTestCase[] },
): Record<string, unknown> {
  if (typeof row.input_str !== "string") {
    return row
  }
  const saved = testCases.find((testCase) => testCase.id === row.id)
  return {
    ...row,
    input_str: separateAppendedText(saved?.input_str, row.input_str),
  }
}

function testCasesNoun(count: number) {
  return `${count} test case${count === 1 ? "" : "s"}`
}

/**
 * The per-batch receipt, never the editor's cumulative pending total: pending
 * rows can predate the current requested_total request (a one-shot draft left
 * unsaved), so a running total would over-count and hand Sofia false progress.
 * Cumulative gating derives from the transcript's batch sizes instead (see
 * app-covi draftBatchProgress). It names every skipped row so the user or
 * Sofia can retry them, and must END with "staged for review": app-covi
 * chatTurnPayload classifies a settled action as staged (not done) by that
 * tail, before it clamps the text for history.
 */
function stagedReceipt(
  total: number,
  newlyStaged: number,
  already: number,
  skipped: string[],
  scripted: string[],
) {
  const count = newlyStaged + already
  const notes = [
    skipped.length > 0
      ? `${skipped.length} already match the proposed values or no longer exist (${skipped.join(", ")})`
      : "",
    scripted.length > 0
      ? `${scripted.length} ${scripted.length === 1 ? "is a scripted case" : "are scripted cases"}, whose exact turns take no appended text (${scripted.join(", ")})`
      : "",
    already > 0 && newlyStaged > 0
      ? `${already} ${already === 1 ? "was" : "were"} already staged`
      : "",
  ].filter(Boolean)
  const prefix = notes.length > 0 ? `${notes.join("; ")}; ` : ""
  if (count < total) {
    return `${prefix}${count} of ${total} test cases staged for review`
  }
  // Only the scripted note can reach this arm; keep it so a retry still says
  // which append was dropped.
  return newlyStaged === 0
    ? `${prefix}${testCasesNoun(count)} already staged for review`
    : `${prefix}${testCasesNoun(count)} staged for review`
}

/**
 * An append aimed at a scripted case, whose input is its exact turns. The
 * case's effective type counts: a pending conversion to SCRIPT makes it one.
 */
function isScriptedAppend(
  row: Record<string, unknown>,
  pending: PendingChanges | null,
  { testCases }: { testCases: EditableTestCase[] },
): boolean {
  if (typeof row.append_input_str !== "string") {
    return false
  }
  const pendingEdit =
    pending?.type === "EDIT"
      ? pending.data.find((edit) => edit.id === row.id)
      : undefined
  // An explicit pending type wins; without one, pending script turns make it a
  // script before the type change lands (a conversion back to SCENARIO can
  // leave stale turns behind, which then mean nothing).
  if (typeof pendingEdit?.input_type === "string") {
    return pendingEdit.input_type === "SCRIPT"
  }
  if (Array.isArray(pendingEdit?.script_turns)) {
    return true
  }
  return (
    testCases.find((testCase) => testCase.id === row.id)?.input_type ===
    "SCRIPT"
  )
}

/** A scripted row without its append; null when nothing else is left to stage. */
function withoutAppend(
  row: Record<string, unknown>,
): Record<string, unknown> | null {
  const { append_input_str: _append, ...rest } = row
  return Object.entries(rest).some(
    ([key, value]) => key !== "id" && value !== undefined,
  )
    ? rest
    : null
}

function findTestCaseRow(id: string): HTMLElement | null {
  return (
    Array.from(
      document.querySelectorAll<HTMLElement>("[data-test-case-id]"),
    ).find((element) => element.dataset.testCaseId === id) ?? null
  )
}

export function CoviTestSetEditorBridge() {
  const { testSetId, testCases, pendingChanges } = useTestSetEditorContext()
  const dispatch = useEventDispatcher()
  const registrationRef = useRef<symbol | null>(null)
  const stateRef = useRef({ testSetId, testCases, pendingChanges })
  stateRef.current = { testSetId, testCases, pendingChanges }

  // Sofia auto-executes every confirm card from one reply inside a single
  // effect flush, so `stage` runs back to back with no render in between and
  // `pendingChanges` above is still the pre-batch value on every call after the
  // first. Accumulate onto what we last dispatched instead, and drop that the
  // moment the editor renders anything other than it — a save, an undo, or a
  // manual row edit all make the editor authoritative again.
  const stagedRef = useRef<PendingChanges | null>(null)
  if (stagedRef.current !== null && pendingChanges !== stagedRef.current) {
    stagedRef.current = null
  }

  useEffect(() => {
    const stage = (
      action: CoviTestSetEditorAction,
    ): CoviTestSetEditorResult => {
      const current = stateRef.current
      const basePending = stagedRef.current ?? current.pendingChanges
      const actionType = actionTypeFor(action)
      const pendingType = pendingTypeFor(action)
      if (basePending && basePending.type !== pendingType) {
        return {
          ok: false,
          error:
            "Save or undo the current test-case changes before staging a different kind of change.",
        }
      }

      const rows = rowsFor(action)

      let nextPending = basePending
      const stagedIds: string[] = []
      // Rows already staged with exactly these values: a repeated proposal or a
      // Retry. They count as staged, so staging the same change twice is a
      // no-op that still reports success instead of a false failure.
      const alreadyStagedIds: string[] = []
      // Rows the editor refused: already equal to the proposed values, or gone.
      // A batch must say so rather than report a smaller count as success.
      const skipped: string[] = []
      // Appends Sofia aimed at scripted cases: their input is exact turns.
      const scripted: string[] = []
      for (const [index, rawRow] of rows.entries()) {
        // A scripted case drops only the append; its other fields still stage.
        const scriptedRow =
          actionType === "EDIT" &&
          isScriptedAppend(rawRow, nextPending, current)
        if (scriptedRow) {
          scripted.push(String(rawRow.id))
        }
        const remainingRow = scriptedRow ? withoutAppend(rawRow) : rawRow
        if (!remainingRow) {
          continue
        }
        const rowData =
          actionType === "EDIT"
            ? withSeparatedAppend(
                withAppliedAppend(remainingRow, nextPending, current),
                current,
              )
            : remainingRow
        const result = applyPendingRowAction({
          actionType,
          currentPendingChanges: nextPending,
          rowData,
          testCases: current.testCases,
        })
        if (result) {
          nextPending = result.pendingChanges
          if (result.rowMeta.testCaseId) {
            stagedIds.push(result.rowMeta.testCaseId)
          }
          continue
        }
        const id =
          typeof rowData.id === "string" ? rowData.id : `row ${index + 1}`
        const changesSavedCase =
          actionType !== "ADD_NEW" &&
          applyPendingRowAction({
            actionType,
            currentPendingChanges: null,
            rowData,
            testCases: current.testCases,
          }) !== null
        if (changesSavedCase) {
          alreadyStagedIds.push(id)
        } else {
          skipped.push(id)
        }
      }

      if (stagedIds.length === 0 && alreadyStagedIds.length > 0) {
        return {
          ok: true,
          detail: stagedReceipt(
            rows.length,
            0,
            alreadyStagedIds.length,
            skipped,
            scripted,
          ),
        }
      }

      if (
        scripted.length > 0 &&
        stagedIds.length === 0 &&
        alreadyStagedIds.length === 0
      ) {
        return {
          ok: false,
          error:
            skipped.length === 0
              ? "Sofia can't append text to scripted test cases: their input is exact turns. Edit their script turns instead."
              : `Sofia couldn't stage any of the ${rows.length} test cases: ${scripted.length} ${scripted.length === 1 ? "is a scripted case" : "are scripted cases"}, whose exact turns take no appended text (${scripted.join(", ")}), and ${skipped.length} already match the proposed values or no longer exist (${skipped.join(", ")}).`,
        }
      }

      if (!nextPending || stagedIds.length === 0) {
        return {
          ok: false,
          error:
            rows.length > 1
              ? `Sofia couldn't stage any of the ${rows.length} test cases: each already matches the proposed values or no longer exists.`
              : "Sofia couldn't stage that change. The test case may no longer exist or the proposed values may be unchanged.",
        }
      }

      stagedRef.current = nextPending
      dispatch(EVENTS.PENDING_CHANGES_UPDATE, { changes: nextPending })
      const firstId = stagedIds[0]
      window.setTimeout(() => {
        const row = findTestCaseRow(firstId)
        row?.scrollIntoView({ behavior: "smooth", block: "center" })
        requestCoviHighlight(firstId)
      }, 0)

      return {
        ok: true,
        detail: stagedReceipt(
          rows.length,
          stagedIds.length,
          alreadyStagedIds.length,
          skipped,
          scripted,
        ),
      }
    }

    const { registration, unregister } = registerCoviTestSetEditor({
      stage,
      save: () => {
        const count = stateRef.current.pendingChanges?.data.length ?? 0
        if (count === 0) {
          return {
            ok: false,
            error: "There are no staged test-case changes to save.",
          }
        }
        dispatch(EVENTS.SAVE_REQUEST, {})
        return {
          ok: true,
          detail: `Saving ${count} staged test case${count === 1 ? "" : "s"}`,
        }
      },
      undoAll: () => {
        dispatch(EVENTS.PENDING_CHANGES_UPDATE, { changes: null })
      },
      undoItem: (index) => {
        const current = stateRef.current.pendingChanges
        dispatch(EVENTS.PENDING_CHANGES_UPDATE, {
          changes: removePendingChangeAtIndex(current, index),
        })
      },
      scrollToItem: (id) => {
        const row = findTestCaseRow(id)
        row?.scrollIntoView({ behavior: "smooth", block: "center" })
        requestCoviHighlight(id)
      },
    })
    registrationRef.current = registration
    return unregister
  }, [dispatch])

  const snapshot = useMemo(() => {
    const diffs = getPendingDiffSummaries(pendingChanges, testCases) ?? {}
    const items = pendingChanges
      ? pendingChanges.data.map((entry, index) => {
          const id: string =
            pendingChanges.type === "DELETE"
              ? (entry as string)
              : (entry as { id: string }).id
          const pendingTestCase =
            pendingChanges.type === "ADD"
              ? (entry as EditableTestCase)
              : testCases.find((candidate) => candidate.id === id)
          return {
            id,
            label: testCaseLabel(pendingTestCase, index),
            added: diffs[id]?.added ?? 0,
            removed: diffs[id]?.removed ?? 0,
          }
        })
      : []
    return {
      active: true,
      testSetId,
      testCaseIds: testCases.map((testCase) => testCase.id),
      pendingType: pendingChanges?.type ?? null,
      items,
    }
  }, [pendingChanges, testCases, testSetId])

  useEffect(() => {
    if (registrationRef.current) {
      publishCoviTestSetEditorSnapshot(registrationRef.current, snapshot)
    }
  }, [snapshot])

  return null
}
